import { useEffect, useMemo, useState } from 'react';
import type { CalendarEvent } from '../types/calendar';
import {
  calendarLinkResolver,
  type CalendarLinkResolver,
  type CalendarLinkSource,
  type CalendarLinkSummary,
} from '../services/calendarLinkSources';

/**
 * Which of the given events are linked from a team module, keyed by event id.
 * Only linked events appear in the map.
 *
 * Lookups run in the background through the shared, session-cached resolver,
 * so the calendar renders immediately and linked events settle into their
 * quieter style as answers arrive. Results are flushed in small batches rather
 * than one render per response.
 */
export function useCalendarLinks(
  events: readonly CalendarEvent[],
  sources: readonly CalendarLinkSource[],
  resolver: CalendarLinkResolver = calendarLinkResolver,
): Map<string, CalendarLinkSummary> {
  // Bumped whenever the resolver's cache gained answers for the current ids.
  const [version, setVersion] = useState(0);

  const idsKey = useMemo(() => Array.from(new Set(events.map(event => event.id))).sort().join('\n'), [events]);
  const sourcesKey = sources.join(',');

  useEffect(() => {
    if (!idsKey || !sourcesKey) return;
    const requested = sourcesKey.split(',') as CalendarLinkSource[];
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleFlush = () => {
      if (cancelled || timer != null) return;
      timer = setTimeout(() => {
        timer = null;
        if (!cancelled) setVersion(current => current + 1);
      }, 80);
    };

    for (const id of idsKey.split('\n')) {
      if (resolver.peek(id, requested)) continue;
      void resolver.resolve(id, requested).then(scheduleFlush);
    }

    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [idsKey, sourcesKey, resolver]);

  return useMemo(() => {
    const linked = new Map<string, CalendarLinkSummary>();
    if (!idsKey || !sourcesKey) return linked;
    const requested = sourcesKey.split(',') as CalendarLinkSource[];
    for (const id of idsKey.split('\n')) {
      const summary = resolver.peek(id, requested);
      if (summary && summary.targets.length > 0) linked.set(id, summary);
    }
    return linked;
    // `version` is the signal that the resolver cache changed for these ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, sourcesKey, resolver, version]);
}
